"use client";

import React, {useState} from "react";
import {FaLock} from "react-icons/fa";
import LocalizedLinkClient from "components/LocalizedLink/LocalizedLinkClient";
import {useAxios} from "hooks/useAxios";
import {getClientSideCookie, useTekstClient} from "hooks/useTekstClient";
import {useContactPrefill} from "./useContactPrefill";

function resolveActiveLocale(fallback) {
    if (typeof window !== "undefined") {
        const fromPath = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})(?=\/|$)/i)?.[1];
        if (fromPath) return fromPath;
    }

    const fromCookie = getClientSideCookie("NEXT_LOCALE");
    if (fromCookie) return fromCookie;

    return fallback;
}

const CONTACT_HERO_URL = "https://share.mkg.eu/web/heroAddOnContactForm.png";

const isPrefilled = (value) => typeof value === "string" && value.trim().length > 0;
const FALLBACK_DEBI_NUM = "313033";
const PHONE_PATTERN = /^(\+?[0-9]+)?$/;

function sanitizePhoneInput(value) {
    const cleaned = value.replace(/[^\d+]/g, "");
    const hasLeadingPlus = cleaned.startsWith("+");
    const digits = cleaned.replace(/\D/g, "");
    return hasLeadingPlus ? `+${digits}` : digits;
}

function LockedField({
    id,
    name,
    label,
    type = "text",
    required = false,
    readOnly = false,
    defaultValue = "",
    pattern,
    inputMode,
    maxLength,
    onInput,
    title,
}) {
    return (
        <>
            <label
                htmlFor={id}
                className={
                    readOnly
                        ? "flex items-center justify-start gap-[6px] mb-1 w-full leading-[1.2] min-h-[calc(1.2em+2px)] text-darkBlack text-xs font-bold"
                        : "block text-left mb-1 w-full leading-[1.2] min-h-[calc(1.2em+2px)] text-darkBlack text-xs font-bold"
                }
            >
                {readOnly ? (
                    <>
                        <span className="inline-flex items-center justify-center shrink-0 w-[14px] h-[1.2em]">
                            <FaLock className="block shrink-0 text-[11px] text-darkBlack opacity-70" aria-hidden />
                        </span>
                        <span className="flex-[0_1_auto] min-w-0 text-left text-darkBlack opacity-70">{label}</span>
                    </>
                ) : (
                    label
                )}
            </label>
            <input
                id={id}
                name={name ?? id}
                type={type}
                required={required}
                readOnly={readOnly}
                defaultValue={defaultValue}
                pattern={pattern}
                inputMode={inputMode}
                maxLength={maxLength}
                onInput={onInput}
                title={title}
                className="w-full border-0 rounded-[4px] py-2 px-[10px] text-[13px] text-darkBlack bg-lightGrey2 mb-2 read-only:bg-lightGrey2 read-only:opacity-80 read-only:cursor-not-allowed read-only:pointer-events-none"
            />
        </>
    );
}

export default function ContactForm({lang, id, titel}) {
    const {tekst} = useTekstClient();
    const {post} = useAxios();
    const {isLoggedIn, prefill, debiNum, bedrijfId} = useContactPrefill();

    const activeLocale = resolveActiveLocale(lang);
    const returnAfterLoginPath = `/${activeLocale}/mkg-add-on/${id}/${titel}/contact`;
    const loginHref = `/mijn-mkg/inloggen?intended=${encodeURIComponent(returnAfterLoginPath)}`;

    const [submitState, setSubmitState] = useState("idle");
    const [fieldErrors, setFieldErrors] = useState({});
    const [licenseCheckState, setLicenseCheckState] = useState("idle");

    const handlePhoneInput = (event) => {
        event.target.value = sanitizePhoneInput(event.target.value);
    };

    const handleLicenseCheck = async () => {
        setLicenseCheckState("loading");

        try {
            const response = await post(`/mkg-add-ons/${id}/${titel}/license-check`, {
                ...(bedrijfId ? {bedrijf_id: bedrijfId} : {}),
                ...(debiNum ? {debi_num: debiNum} : {}),
            });
            setLicenseCheckState(response.data?.compatible ? "compatible" : "incompatible");
        } catch {
            setLicenseCheckState("error");
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFieldErrors({});

        const formEl = event.currentTarget;
        const fd = new FormData(formEl);
        const phoneNumber = String(fd.get("phoneNumber") ?? "").trim();

        if (phoneNumber && !PHONE_PATTERN.test(phoneNumber)) {
            setFieldErrors({
                phoneNumber: "Phone number may only contain numbers, with an optional + as the first character.",
            });
            return;
        }

        setSubmitState("loading");

        try {
            await post(`/mkg-add-ons/${id}/${titel}/contact`, {
                debi_num: debiNum ?? FALLBACK_DEBI_NUM,
                form: {
                    companyName: String(fd.get("companyName") ?? ""),
                    companyLocation: String(fd.get("companyLocation") ?? ""),
                    emailAddress: String(fd.get("emailAddress") ?? ""),
                    phoneNumber,
                    moreInfo: String(fd.get("moreInfo") ?? ""),
                },
            });

            setSubmitState("success");
            formEl.reset();
        } catch (e) {
            if (e?.response?.status === 422 && e?.response?.data?.errors?.["form.phoneNumber"]) {
                const messages = e.response.data.errors["form.phoneNumber"];
                setFieldErrors({
                    phoneNumber: Array.isArray(messages) ? messages[0] : String(messages),
                });
            }
            setSubmitState("error");
        }
    };

    const actionButtonClassName =
        "w-full min-h-[32px] border-0 rounded-[4px] bg-lightYellow text-darkBlack text-base font-bold inline-flex items-center justify-center box-border cursor-pointer !no-underline hover:!no-underline";

    return (
        <div className="bg-white">
            <div
                className="relative overflow-hidden h-[380px] max-[600px]:h-[260px] bg-cover bg-no-repeat bg-[position:75%_center]"
                style={{backgroundImage: `url("${CONTACT_HERO_URL}")`}}
                role="img"
                aria-hidden
            />

            <section className="w-full bg-white px-4 pt-9 pb-[42px] max-[600px]:px-3 max-[600px]:pt-6 max-[600px]:pb-[30px]">
                <p className="w-[min(760px,100%)] mx-auto mb-[26px] text-darkBlack text-center text-base leading-normal">
                    {tekst("mkgeu:mkgaddons:contact:topText")}
                </p>

                <form className="w-[min(560px,100%)] mx-auto" onSubmit={handleSubmit}>
                    <LockedField
                        id="companyName"
                        name="companyName"
                        label={<>* {tekst("mkgeu:mkgaddons:contact:companyName")}</>}
                        type="text"
                        required
                        readOnly={isPrefilled(prefill.companyName)}
                        defaultValue={prefill.companyName}
                    />

                    <LockedField
                        id="companyLocation"
                        name="companyLocation"
                        label={<>* {tekst("mkgeu:mkgaddons:contact:companyLocation")}</>}
                        type="text"
                        required
                        readOnly={isPrefilled(prefill.companyLocation)}
                        defaultValue={prefill.companyLocation}
                    />

                    <div className="grid grid-cols-2 max-[600px]:grid-cols-1 gap-2 items-start [&>*]:flex [&>*]:flex-col [&>*]:items-stretch [&>*]:min-w-0">
                        <div>
                            <LockedField
                                id="emailAddress"
                                name="emailAddress"
                                label={<>* {tekst("mkgeu:mkgaddons:contact:emailAddress")}</>}
                                type="email"
                                required
                                readOnly={isPrefilled(prefill.email)}
                                defaultValue={prefill.email}
                            />
                        </div>
                        <div>
                            <LockedField
                                id="phoneNumber"
                                name="phoneNumber"
                                label={tekst("mkgeu:mkgaddons:contact:phoneNumber")}
                                type="text"
                                readOnly={false}
                                defaultValue=""
                                inputMode="tel"
                                maxLength={25}
                                onInput={handlePhoneInput}
                            />
                            {fieldErrors.phoneNumber ? (
                                <p className="mt-[-6px] mb-2 text-xs text-red-600" role="alert">
                                    {fieldErrors.phoneNumber}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    <label htmlFor="moreInfo" className="block text-darkBlack text-xs font-bold mb-1">
                        * {tekst("mkgeu:mkgaddons:contact:moreInfo")} ({titel.toUpperCase()}) 
                    </label>
                    <textarea
                        id="moreInfo"
                        name="moreInfo"
                        required
                        className="w-full border-0 rounded-[4px] py-2 px-[10px] text-[13px] text-darkBlack bg-lightGrey2 mb-2 min-h-[170px] resize-y"
                    />

                    <button
                        type="submit"
                        disabled={submitState === "loading"}
                        className="w-full min-h-[40px] mt-[6px] border-0 rounded-[4px] bg-lightYellow text-darkBlack text-sm max-[600px]:text-lg font-extrabold uppercase cursor-pointer disabled:opacity-70 disabled:cursor-wait"
                    >
                        {tekst("mkgeu:mkgaddons:contact:submit")}
                    </button>

                    {submitState === "success" ? (
                        <p
                            className="w-[min(760px,100%)] mx-auto mt-[14px] text-darkBlack text-center text-base leading-normal"
                            aria-live="polite"
                        >
                            {tekst("mkgeu:mkgaddons:contact:success")}
                        </p>
                    ) : null}
                    {submitState === "error" ? (
                        <p
                            className="w-[min(760px,100%)] mx-auto mt-[14px] text-darkBlack text-center text-base leading-normal"
                            aria-live="polite"
                        >
                            {tekst("mkgeu:mkgaddons:contact:error")}
                        </p>
                    ) : null}
                </form>

                <div className="w-[min(560px,100%)] mx-auto mt-4">
                    {!isLoggedIn ? (
                        <LocalizedLinkClient
                            href={loginHref}
                            className={actionButtonClassName}
                            style={{textDecoration: "none"}}
                        >
                            {tekst("mkgeu:mkgaddons:contact:login")}
                        </LocalizedLinkClient>
                    ) : (
                        <button
                            type="button"
                            className={actionButtonClassName}
                            onClick={handleLicenseCheck}
                            disabled={licenseCheckState === "loading"}
                        >
                            {tekst("mkgeu:mkgaddons:contact:licenseCheck")}
                        </button>
                    )}
                </div>

                {licenseCheckState === "compatible" ? (
                    <p
                        className="w-[min(760px,100%)] mx-auto mt-[14px] text-darkBlack text-center text-base leading-normal"
                        role="status"
                        aria-live="polite"
                    >
                        {tekst("mkgeu:mkgaddons:contact:licenseCompatible")}
                    </p>
                ) : null}
                {licenseCheckState === "incompatible" ? (
                    <p
                        className="w-[min(760px,100%)] mx-auto mt-[14px] text-darkBlack text-center text-base leading-normal"
                        role="status"
                        aria-live="polite"
                    >
                        {tekst("mkgeu:mkgaddons:contact:licenseIncompatible")}
                    </p>
                ) : null}
                {licenseCheckState === "error" ? (
                    <p
                        className="w-[min(760px,100%)] mx-auto mt-[14px] text-darkBlack text-center text-base leading-normal"
                        role="alert"
                        aria-live="polite"
                    >
                        {tekst("mkgeu:mkgaddons:contact:licenseCheckError")}
                    </p>
                ) : null}

                {isLoggedIn ? (
                    <p className="w-[min(760px,100%)] mx-auto mt-[14px] text-darkBlack text-center text-base leading-normal">
                        {tekst("mkgeu:mkgaddons:contact:bottomText")}
                    </p>
                ) : null}
            </section>
        </div>
    );
}
